package org.example.zone360.utils.mediaManagerUtils

import org.example.zone360.utils.Config
import org.w3c.dom.Audio

object AudioManager {

    private const val MAX_CACHED = Config.MAX_CACHED

    private val cache = mutableMapOf<String, Audio>()
    private var activeUrl: String? = null
    private val fullyDownloaded = LinkedHashSet<String>() // preserves insertion order for LRU
    private val accessOrder = ArrayDeque<String>()        // tracks access order for cache eviction

    fun prepare(url: String): Audio {
        val current = activeUrl
        if (current != null && current != url) {
            suspendDownload(current)
        }

        activeUrl = url

        // Update access order for LRU
        accessOrder.remove(url)
        accessOrder.addLast(url)

        // Evict oldest if over limit
        while (accessOrder.size > MAX_CACHED) {
            val evicted = accessOrder.removeFirst()
            cache[evicted]?.let { audio ->
                audio.pause()
                audio.src = ""
                audio.load()
            }
            cache.remove(evicted)
            fullyDownloaded.remove(evicted)
        }

        val existing = cache[url]
        if (existing != null && existing.src.isNotEmpty()) {
            return existing
        }

        val audio = Audio(url)
        cache[url] = audio

        audio.addEventListener("canplaythrough", {
            fullyDownloaded.add(url)
        })

        return audio
    }

    fun suspendDownload(url: String) {
        val audio = cache[url] ?: return

        if (url in fullyDownloaded) return

        audio.pause()
        audio.src = ""
        audio.load()
        cache.remove(url)

        if (activeUrl == url) activeUrl = null
    }

    fun release(url: String) {
        val audio = cache[url]
        audio?.pause()
        audio?.src = ""
        audio?.load()
        cache.remove(url)
        fullyDownloaded.remove(url)
        accessOrder.remove(url)
        if (activeUrl == url) activeUrl = null
    }

    fun releaseAll() {
        cache.values.forEach { audio ->
            audio.pause()
            audio.src = ""
            audio.load()
        }
        cache.clear()
        accessOrder.clear()
        activeUrl = null
    }

    fun isReady(url: String): Boolean = url in fullyDownloaded
    fun isCached(url: String): Boolean = cache.containsKey(url)
}